import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { getWebrootUrl } from '../utils';

export type Theme = 'light' | 'dark' | 'auto' | string;

interface PublicThemeContextType {
  theme: Theme;
  isDark: boolean;
}

const PublicThemeContext = createContext<PublicThemeContextType | undefined>(undefined);

export function usePublicTheme() {
  const context = useContext(PublicThemeContext);
  if (context === undefined) {
    throw new Error('usePublicTheme must be used within a PublicThemeProvider');
  }
  return context;
}

interface PublicThemeProviderProps {
  children: React.ReactNode;
}

export function PublicThemeProvider({ children }: PublicThemeProviderProps) {
  const theme: Theme = 'auto'; // Always use auto theme for public views
  const [isDark, setIsDark] = useState(false);
  const currentThemeLinkRef = useRef<HTMLLinkElement | null>(null);

  const loadThemeCSS = useCallback((themeName: string) => {
    // Remove existing theme CSS
    if (currentThemeLinkRef.current) {
      document.head.removeChild(currentThemeLinkRef.current);
      currentThemeLinkRef.current = null;
    }

    // Only load CSS for custom themes (not light/dark as they use Tailwind defaults)
    if (themeName !== 'light' && themeName !== 'dark') {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      // Detect webroot from URL for theme CSS
      const pathname = window.location.pathname;
      const segments = pathname.split('/').filter(Boolean);
      let webroot = '';
      
      if (segments.length > 0) {
        const firstSegment = segments[0].toLowerCase();
        const webrootPatterns = ['samler', 'comics', 'app', 'collection'];
        if (webrootPatterns.includes(firstSegment)) {
          webroot = `/${segments[0]}`;
        }
      }
      
      link.href = `${getWebrootUrl(webroot)}/themes/${themeName}.css`;
      link.onload = () => {
        // Apply theme class after CSS loads
        document.documentElement.classList.remove('dark', 'light', 'custom-theme');
        document.documentElement.classList.add('custom-theme');
      };
      document.head.appendChild(link);
      currentThemeLinkRef.current = link;
    }
  }, []);

  useEffect(() => {
    const updateTheme = () => {
      // Auto theme: use system preference
      const shouldBeDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

      setIsDark(shouldBeDark);
      
      // Remove all theme classes first
      document.documentElement.classList.remove('dark', 'light', 'custom-theme');
      
      // For auto mode, use default Tailwind classes
      if (shouldBeDark) {
        document.documentElement.classList.add('dark');
      }
      
      // Remove any custom theme CSS
      if (currentThemeLinkRef.current) {
        document.head.removeChild(currentThemeLinkRef.current);
        currentThemeLinkRef.current = null;
      }
    };

    updateTheme();

    // Listen for system color scheme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => updateTheme();
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [loadThemeCSS]);

  const contextValue = useMemo(() => ({ theme, isDark }), [theme, isDark]);

  return (
    <PublicThemeContext.Provider value={contextValue}>
      {children}
    </PublicThemeContext.Provider>
  );
}